import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import PageSEO from "@/components/PageSEO";
import { breadcrumb } from "@/lib/schemas";
import ScheduleNav from "@/components/ScheduleNav";
import ChoosePathModal from "@/components/ChoosePathModal";
import { Button } from "@/components/ui/button";
import { Bot, MessageSquare, Zap, Clock, Cog, ArrowLeft } from "lucide-react";

const Day3 = () => {
  const [modalOpen, setModalOpen] = useState(false);

  const walkAwayWith = [
    { text: "A trained assistant in your voice", icon: MessageSquare },
    { text: "A lead-response workflow you can reuse", icon: Zap },
    { text: "A client support assistant that saves hours each week", icon: Clock },
    { text: "A setup you can keep building after the summit", icon: Cog },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#fffcf2] font-sans">
      <PageSEO
        title="Day 3 · Voice Bots & Automation | Tech Founder Summit"
        description="Day 3 of the Tech Founder Summit. Train an assistant in your voice, build a lead-response workflow, and ship a client support bot that saves hours."
        canonical="https://marquettabryant.com/day-3"
        jsonLd={[breadcrumb([{name:"Home",path:"/"},{name:"Tech Founder Summit",path:"/tech-founder-summit"},{name:"Day 3",path:"/day-3"}])]}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#2e4053] to-[#1a1a2e] pt-8 pb-16 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-64 h-64 bg-[#1e90ff]/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-20 w-80 h-80 bg-[#d8239b]/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Back Link */}
          <a 
            href="https://marquettabryant.com/tech-founder-summit" 
            className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            ← Back to Summit Home
          </a>
          
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1e90ff]/20 rounded-full mb-6">
              <Bot className="w-5 h-5 text-[#1e90ff]" />
              <span className="text-[#1e90ff] font-semibold text-sm">DAY 3 • FEB 12</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Build Your AI Assistant
            </h1>
            
            <p className="text-xl md:text-2xl text-white/80 mb-10 max-w-2xl mx-auto">
              Leave with an assistant trained in your voice, ready to work.
            </p>
            
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-[#d8239b] to-[#ff69b4] hover:from-[#d8239b]/90 hover:to-[#ff69b4]/90 text-white font-bold px-10 py-7 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
              onClick={() => setModalOpen(true)}
            >
              Choose Your Path
            </Button>
          </div>
        </div>
      </section>
      
      <ScheduleNav />
      
      <main className="pt-12 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Outcome & Tagline Card */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-10 mb-8">
              {/* Outcome Badge */}
              <div className="bg-gradient-to-r from-[#d8239b]/10 to-[#ff69b4]/10 rounded-2xl p-6 mb-6 border border-[#d8239b]/20">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-3 py-1 bg-[#d8239b] text-white text-xs font-bold rounded-full">OUTCOME</span>
                </div>
                <p className="text-lg md:text-xl text-[#2e4053] font-medium">
                  Leave with your own AI assistant working inside your business.
                </p>
              </div>

              {/* Tagline */}
              <div className="text-center">
                <p className="text-2xl text-[#d8239b] font-bold italic">
                  Stop borrowing tools and start building systems.
                </p>
              </div>
            </div>

            {/* Walk Away With Card */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-10 mb-10">
              <h3 className="text-xl font-bold text-[#2e4053] mb-6">You Will Walk Away With</h3>
              <div className="space-y-4">
                {walkAwayWith.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 p-5 bg-gradient-to-r from-[#fffcf2] to-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#d8239b] to-[#ff69b4] flex items-center justify-center flex-shrink-0 shadow-md">
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-[#2e4053] font-medium text-lg">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mb-10">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-[#d8239b] to-[#ff69b4] hover:from-[#d8239b]/90 hover:to-[#ff69b4]/90 text-white font-bold px-12 py-7 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
                onClick={() => setModalOpen(true)}
              >
                Choose Your Path
              </Button>
            </div>

            {/* Bottom Back Link */}
            <div className="text-center">
              <a 
                href="https://marquettabryant.com/tech-founder-summit" 
                className="inline-flex items-center gap-2 text-[#2e4053]/70 hover:text-[#d8239b] transition-colors font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                ← Back to Summit Home
              </a>
            </div>
          </div>
        </div>
      </main>

      <ChoosePathModal open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
};

export default Day3;
